import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isoADisplayDDMMAAAA } from './fechaReporte';

const formatFechaDMY = (iso) => (iso ? isoADisplayDDMMAAAA(String(iso).split('T')[0]) : '—');

const excelSheetName = (title) => {
  const s = String(title ?? 'Reporte')
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim()
    .slice(0, 31);
  return s || 'Reporte';
};

/**
 * Excel: título del reporte, periodo, cajas/turnos y tabla (mismas columnas que la vista web).
 * @param {string} title título visible (ej. "Ventas por hora")
 * @param {object[]} rows
 * @param {{ fechaInicio?: string, fechaFin?: string, cajaInicio?: number, cajaFin?: number, turnoInicio?: number, turnoFin?: number }} [options]
 */
export function buildTabularReportExcelAoa(title, rows, options = {}) {
  const {
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
  } = options;

  const aoa = [];
  aoa.push([title]);
  aoa.push([`Periodo: ${fechaInicio} al ${fechaFin}`]);
  aoa.push([
    `Cajas: ${cajaInicio ?? '—'} a ${cajaFin ?? '—'} · Turnos: ${turnoInicio ?? '—'} a ${turnoFin ?? '—'}`,
  ]);
  aoa.push([]);

  if (!Array.isArray(rows) || rows.length === 0) {
    aoa.push(['Sin datos para el rango seleccionado.']);
    return aoa;
  }

  const headers = Object.keys(rows[0]);
  aoa.push(headers);

  for (const row of rows) {
    aoa.push(
      headers.map((h) => {
        const v = row[h];
        if (v == null || v === '') return '';
        if (typeof v === 'object') return JSON.stringify(v);
        return v;
      })
    );
  }

  return aoa;
}

const fmtCell = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString('es-MX', { maximumFractionDigits: 2 });
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * PDF en cliente (formato reporte), alineado con mesero/ventas por hora: portada + tabla.
 * @param {string} title
 * @param {object[]} rows
 * @param {{ fechaInicio?: string, fechaFin?: string, cajaInicio?: number, cajaFin?: number, turnoInicio?: number, turnoFin?: number, empresaLine?: string, fileBase?: string }} [options] fileBase slug para el nombre del archivo (ej. endpoint)
 */
export function downloadTabularReportPdf(title, rows, options = {}) {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    empresaLine = 'Reporte operativo · SMARTNET',
    fileBase = 'reporte',
  } = options;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(empresaLine, pageW / 2, y, { align: 'center' });
  y += 6;
  doc.setFontSize(11);
  doc.text(String(title), pageW / 2, y, { align: 'center' });
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(
    `Del: ${formatFechaDMY(fechaInicio)}  al  ${formatFechaDMY(fechaFin)}`,
    pageW / 2,
    y,
    { align: 'center' }
  );
  y += 4;
  doc.text(
    `Cajas: ${cajaInicio ?? '—'}  a  ${cajaFin ?? '—'}    ·    Turnos: ${turnoInicio ?? '—'}  a  ${turnoFin ?? '—'}`,
    pageW / 2,
    y,
    { align: 'center' }
  );
  y += 10;
  doc.setTextColor(0);

  const headers = Object.keys(rows[0]);
  const body = rows.map((row) => headers.map((h) => fmtCell(row[h])));

  autoTable(doc, {
    startY: y,
    head: [headers],
    body,
    theme: 'striped',
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [226, 232, 240],
      fontStyle: 'bold',
      halign: 'center',
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { fontSize: 7.5, cellPadding: 1.5, textColor: [15, 23, 42] },
    margin: { left: margin, right: margin },
  });

  const totalPages = doc.internal.getNumberOfPages();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text(`Generado ${new Date().toLocaleString('es-MX')}`, margin, ph - 8);
    doc.text(`Página ${i} de ${totalPages}`, pageW - margin, ph - 8, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(`${fileBase}-${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`);
}

export { excelSheetName };
